import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSpace, useSpaceMembers, useSpacePages, useUpdateSpace, useDeleteSpace, useAddMember, useRemoveMember, useUpdateMemberRole } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton, Input, Label } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { ArrowLeft, Settings, Users, Trash2, Shield, UserPlus, X, FileText, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatRelativeTime } from '@/lib/utils';

export function SpaceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: space, isLoading, error } = useSpace(slug!);
  const { data: members, isLoading: membersLoading } = useSpaceMembers(slug!);
  const { data: pagesData, isLoading: pagesLoading } = useSpacePages(slug!);
  const { user } = useAuthStore();
  const deleteSpace = useDeleteSpace();

  const isAdmin = space?.user_role === 'admin';

  const handleDelete = () => {
    if (!confirm('确定要删除此空间吗？此操作不可撤销。')) return;

    deleteSpace.mutate(slug!, {
      onSuccess: () => {
        toast.success('空间已删除');
        window.location.href = '/spaces';
      },
      onError: (error: any) => {
        toast.error(error.message || '删除失败');
      },
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !space) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-red-500 mb-4">空间不存在或无权访问</p>
        <Link to="/spaces">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回空间列表
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-12 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/spaces">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{space.name}</h1>
            {space.description && (
              <p className="text-muted-foreground mt-1">{space.description}</p>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <EditSpaceDialog space={space} slug={slug!} />
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              删除空间
            </Button>
          </div>
        )}
      </div>

      {/* Pages in Space */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            页面 ({pagesData?.total || 0})
          </CardTitle>
          {isAdmin && (
            <Link to={`/pages/new?space=${slug}`}>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                新建页面
              </Button>
            </Link>
          )}
        </CardHeader>
        <CardContent>
          {pagesLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !pagesData?.items?.length ? (
            <p className="text-muted-foreground text-center py-4">暂无页面</p>
          ) : (
            <div className="space-y-2">
              {pagesData.items.map((page) => (
                <Link
                  key={page.id}
                  to={`/pages/${page.slug}`}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <div>
                    <p className="font-medium hover:text-primary">{page.title}</p>
                    <p className="text-sm text-muted-foreground">
                      更新于 {formatRelativeTime(page.updated_at)}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    page.visibility === 'anonymous' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    page.visibility === 'public' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    page.visibility === 'editors' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {page.visibility === 'anonymous' ? '匿名' :
                     page.visibility === 'public' ? '公开' :
                     page.visibility === 'editors' ? '编辑者' : '私有'}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                成员 ({members?.length || 0})
              </CardTitle>
              {isAdmin && <AddMemberDialog slug={slug!} />}
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : !members?.length ? (
                <p className="text-muted-foreground text-center py-4">暂无成员</p>
              ) : (
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {member.user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{member.user.username}</p>
                          <p className="text-sm text-muted-foreground">{member.user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            member.role === 'admin'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : member.role === 'member'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {member.role === 'admin' ? '管理员' : member.role === 'member' ? '成员' : '访客'}
                        </span>
                        {isAdmin && member.user.id !== user?.id && (
                          <MemberActions slug={slug!} member={member} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                我的角色
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span
                className={`text-sm px-3 py-1 rounded ${
                  space.user_role === 'admin'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : space.user_role === 'member'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                }`}
              >
                {space.user_role === 'admin' ? '管理员' : space.user_role === 'member' ? '成员' : '访客'}
              </span>
              <div className="mt-4 text-sm text-muted-foreground">
                <p className="mb-1"><strong>管理员</strong>: 可以管理成员和设置</p>
                <p className="mb-1"><strong>成员</strong>: 可以创建和编辑页面</p>
                <p><strong>访客</strong>: 只能查看页面</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function EditSpaceDialog({ space, slug }: { space: any; slug: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(space.name);
  const [description, setDescription] = useState(space.description || '');
  const updateSpace = useUpdateSpace();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSpace.mutate(
      { slug, data: { name, description: description || undefined } },
      {
        onSuccess: () => {
          toast.success('空间已更新');
          setOpen(false);
        },
        onError: (error: any) => {
          toast.error(error.message || '更新失败');
        },
      }
    );
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Settings className="h-4 w-4 mr-2" />
        设置
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>空间设置</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">名称</Label>
                  <Input
                    id="edit-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-desc">描述</Label>
                  <Input
                    id="edit-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={updateSpace.isPending}>
                    保存
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    取消
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

function AddMemberDialog({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const addMember = useAddMember();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Parse user ID from input (could be numeric ID or we need a user search API)
    const userId = parseInt(searchTerm, 10);
    if (isNaN(userId)) {
      toast.error('请输入有效的用户ID（数字）');
      return;
    }

    addMember.mutate(
      { slug, userId, role },
      {
        onSuccess: () => {
          toast.success('成员已添加');
          setOpen(false);
          setSearchTerm('');
          setRole('member');
        },
        onError: (error: any) => {
          toast.error(error.message || '添加失败');
        },
      }
    );
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <UserPlus className="h-4 w-4 mr-2" />
        添加成员
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>添加成员</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-id">用户 ID</Label>
                  <Input
                    id="user-id"
                    type="number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="输入用户ID（数字）"
                  />
                  <p className="text-xs text-muted-foreground">
                    用户可以在个人资料页面查看自己的ID
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">角色</Label>
                  <select
                    id="role"
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    value={role}
                    onChange={(e) => setRole(e.target.value as any)}
                  >
                    <option value="viewer">访客 - 只能查看页面</option>
                    <option value="member">成员 - 可以创建和编辑页面</option>
                    <option value="admin">管理员 - 可以管理成员和设置</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={addMember.isPending}>
                    添加
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    取消
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

function MemberActions({ slug, member }: { slug: string; member: any }) {
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();

  const handleRoleChange = (newRole: 'admin' | 'member' | 'viewer') => {
    updateRole.mutate(
      { slug, userId: member.user_id, role: newRole },
      {
        onSuccess: () => toast.success('角色已更新'),
        onError: (error: any) => toast.error(error.message || '更新失败'),
      }
    );
  };

  const handleRemove = () => {
    if (!confirm('确定要移除此成员吗？')) return;
    removeMember.mutate(
      { slug, userId: member.user_id },
      {
        onSuccess: () => toast.success('成员已移除'),
        onError: (error: any) => toast.error(error.message || '移除失败'),
      }
    );
  };

  return (
    <div className="flex items-center gap-1">
      <select
        className="text-xs px-2 py-1 border rounded bg-background"
        value={member.role}
        onChange={(e) => handleRoleChange(e.target.value as any)}
        disabled={updateRole.isPending}
      >
        <option value="viewer">访客</option>
        <option value="member">成员</option>
        <option value="admin">管理员</option>
      </select>
      <Button variant="ghost" size="sm" onClick={handleRemove} disabled={removeMember.isPending}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
